// /src/components/NavContainer.tsx

// What? A container for the navigation bar.
// Why? To display the navigation bar and the workspace sidebar.
// How?
// - Uses the Upperbar component to display the upper bar.
// - Uses the WorkspaceSidebar component to display the workspace sidebar.
// - Uses the children prop to display the content of the container.

import React, { useState, useEffect } from "react";
import { Box, useTheme } from "@mui/joy";
import { useLocation } from "react-router-dom";
import Upperbar, { UPPERBAR_HEIGHT_PX } from "./Upperbar";
import WorkspaceSidebar from "./WorkspaceSidebar";

interface NavContainerProps {
  children: React.ReactNode;
}

// Custom hook for responsive behavior
const useIsMobile = () => {
  const theme = useTheme();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      // Use theme breakpoints to match Joy UI's xs breakpoint
      const xsBreakpoint = theme.breakpoints.values.xs || 600;
      setIsMobile(window.innerWidth < xsBreakpoint);
    };

    // Check on mount
    checkIsMobile();

    // Add event listener
    window.addEventListener("resize", checkIsMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkIsMobile);
  }, [theme.breakpoints.values.xs]);

  return isMobile;
};

// Routes that should have auto-collapsed sidebar for more workspace
const FULL_WIDTH_ROUTES = [/\/canvases\/[^/]+\/edit$/, /\/canvases\/create$/];

const NavContainer: React.FC<NavContainerProps> = ({ children }) => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOverlayOpen, setIsMobileOverlayOpen] = useState(false);
  const [userCollapsed, setUserCollapsed] = useState(false);
  const sidebarWidth = isMobile ? "0px" : isCollapsed ? "64px" : "250px";
  const isIdaChatRoute = /\/ida\/chat$/.test(location.pathname);

  // Check if current route should have collapsed sidebar
  const isFullWidthRoute = FULL_WIDTH_ROUTES.some((pattern) =>
    pattern.test(location.pathname),
  );

  // Auto-collapse sidebar on mobile when navigating
  useEffect(() => {
    if (isMobile) {
      setIsMobileOverlayOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Set initial state based on screen size and route
  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(true);
    } else if (isFullWidthRoute) {
      // Auto-collapse on full-width routes (like canvas builder)
      setIsCollapsed(true);
    } else if (!userCollapsed) {
      // Restore expanded state when leaving full-width routes
      setIsCollapsed(false);
    }
  }, [isMobile, isFullWidthRoute, userCollapsed]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--workspace-sidebar-width",
      sidebarWidth,
    );
    return () => {
      document.documentElement.style.removeProperty(
        "--workspace-sidebar-width",
      );
    };
  }, [sidebarWidth]);

  const toggleSidebar = () => {
    if (isMobile) {
      setIsMobileOverlayOpen(!isMobileOverlayOpen);
    } else {
      const newCollapsed = !isCollapsed;
      setIsCollapsed(newCollapsed);
      setUserCollapsed(newCollapsed);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        boxSizing: "border-box",
        pt: `${UPPERBAR_HEIGHT_PX}px`,
        "--workspace-sidebar-width": sidebarWidth,
      }}
    >
      <Upperbar onToggleSidebar={toggleSidebar} />
      <Box
        sx={{
          display: "flex",
          flex: 1,
          position: "relative",
          minHeight: 0,
        }}
      >
        {/* Desktop Sidebar */}
        {!isMobile && (
          <>
            <Box
              aria-hidden="true"
              sx={{
                width: sidebarWidth,
                minWidth: sidebarWidth,
                maxWidth: sidebarWidth,
                flexShrink: 0,
                transition:
                  "width 0.3s ease, min-width 0.3s ease, max-width 0.3s ease",
              }}
            />
            <WorkspaceSidebar isCollapsed={isCollapsed} isMobile={false} />
          </>
        )}

        {/* Mobile Sidebar Overlay */}
        {isMobile && isMobileOverlayOpen && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1200,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              animation: "none",
              transition: "none",
            }}
            onClick={() => setIsMobileOverlayOpen(false)}
          >
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                width: "280px",
                maxWidth: "85vw",
                height: "100%",
                backgroundColor: "background.surface",
                borderRight: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                animation: "none",
                transition: "none",
              }}
            >
              <WorkspaceSidebar
                isCollapsed={false}
                isMobile={true}
                onClose={() => setIsMobileOverlayOpen(false)}
              />
            </Box>
          </Box>
        )}

        {/* Main Content */}
        <Box
          component="main"
          key={location.pathname}
          sx={{
            flex: 1,
            backgroundColor: "background.body",
            minWidth: 0,
            minHeight: 0,
            overflowX: "clip",
            overflowY: isIdaChatRoute ? "hidden" : "visible",
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default NavContainer;
